import { useState } from 'react';
import type { ChangeEvent, FormEvent, ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { requestTyped } from '@xdocs/ws-protocol';
import { errorAlert } from '@xdocs/schemas';
import { Button, Input, Mascot, Modal, cn, useI18n, useToast } from '@xdocs/ui';
import { safeT } from '../../i18n/safeT.js';
import { useLogout } from '../../ws/useLogout.js';
import { useWs } from '../../ws/WsProvider.js';
import { useSettingsProfile } from './useSettingsProfile.js';
import { useSettingsT } from './useSettingsT.js';
import styles from './DangerZoneSection.module.css';

/**
 * F10 Danger Zone section — self-designed (no FEATURES.md spec exists for
 * account deletion/export; see `apps/server/src/account/handlers.ts`'s
 * top-of-file comment for the full scope writeup this component's own
 * decisions mirror):
 *
 *  - "Delete account" uses a CUSTOM two-field typed-confirmation form
 *    instead of `@xdocs/ui`'s `ConfirmDialog` — that component only
 *    supports a single asset-name type-to-confirm; a whole-account
 *    deletion's blast radius (per ux.md's "typed confirmation naming the
 *    resource," raised to two fields here) warrants more than one. The
 *    submit button stays disabled until BOTH fields match exactly
 *    (client-side gating only — `account.delete`'s handler re-validates
 *    authoritatively; a client-side pass is UX, never the security boundary).
 *  - Session-ending success needs more than a toast (ux.md: a toast is not
 *    enough for a session-ending action). No dedicated "goodbye" ROUTE
 *    exists and adding one means editing `App.tsx`'s router config — out of
 *    this task's declared footprint (only `SettingsPage.tsx` gets a one-line
 *    JSX addition). Design choice: this section replaces ITS OWN content
 *    with a full inline farewell state on success, held for ~2.5s so the
 *    user actually reads it, THEN calls `useLogout()` (this repo's one
 *    real sign-out path — never hand-rolled) and navigates to `/login`.
 *    The delay is deliberate: `RequireAuth` redirects the instant
 *    `AuthProvider`'s status leaves `'authenticated'`, so calling `logout()`
 *    immediately would yank the farewell message off-screen before anyone
 *    could read it.
 */

/** Exported so the test suite can fast-forward exactly this long rather than guessing/duplicating the constant. */
export const FAREWELL_DISPLAY_MS = 2500;

interface DeleteFieldErrors {
  name?: string;
  email?: string;
}

export function DangerZoneSection(): ReactElement {
  const { locale, t: tGlobal } = useI18n();
  const { t } = useSettingsT();
  const toast = useToast();
  const navigate = useNavigate();
  const logout = useLogout();
  const { wsClient } = useWs();
  const { profile } = useSettingsProfile();

  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [confirmName, setConfirmName] = useState('');
  const [confirmEmail, setConfirmEmail] = useState('');
  const [deleteFieldErrors, setDeleteFieldErrors] = useState<DeleteFieldErrors>({});
  const [deleteBannerError, setDeleteBannerError] = useState<string | undefined>(undefined);
  const [deleting, setDeleting] = useState(false);
  const [farewell, setFarewell] = useState(false);

  const [exportPending, setExportPending] = useState(false);
  const [exportError, setExportError] = useState<string | undefined>(undefined);

  // The confirmation "resource name" — falls back to email when the account
  // never set a display name, mirroring `ProfileSection.tsx`'s identical
  // `profile.displayName ?? profile.email` fallback so every account has a
  // well-defined, typeable name instead of an unconfirmable state.
  const confirmNameTarget = profile ? (profile.displayName ?? profile.email) : '';
  const nameMatches = confirmName.length > 0 && confirmName === confirmNameTarget;
  const emailMatches =
    confirmEmail.trim().length > 0 && profile !== undefined && confirmEmail.trim().toLowerCase() === profile.email.toLowerCase();
  const canSubmitDelete = nameMatches && emailMatches && !deleting;

  function resetDeleteModal(): void {
    setDeleteModalOpen(false);
    setConfirmName('');
    setConfirmEmail('');
    setDeleteFieldErrors({});
    setDeleteBannerError(undefined);
  }

  async function handleConfirmDelete(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canSubmitDelete) return;
    setDeleting(true);
    setDeleteFieldErrors({});
    setDeleteBannerError(undefined);
    try {
      await requestTyped(wsClient, 'account.delete', { confirmDisplayName: confirmName, confirmEmail });
      setDeleteModalOpen(false);
      setFarewell(true);
      // See this file's top-of-file doc comment for why this delay exists —
      // signing out immediately would redirect away before the farewell
      // state ever painted.
      window.setTimeout(() => {
        void (async () => {
          await logout();
          navigate('/login', { replace: true });
        })();
      }, FAREWELL_DISPLAY_MS);
    } catch (err) {
      const parsed = errorAlert.safeParse(err);
      if (parsed.success && parsed.data.code === 'VALIDATION' && parsed.data.fields && parsed.data.fields.length > 0) {
        const nextErrors: DeleteFieldErrors = {};
        for (const field of parsed.data.fields) {
          if (field.path === 'confirmDisplayName') nextErrors.name = safeT(locale, field.message);
          if (field.path === 'confirmEmail') nextErrors.email = safeT(locale, field.message);
        }
        setDeleteFieldErrors(nextErrors);
        if (Object.keys(nextErrors).length === 0) setDeleteBannerError(safeT(locale, parsed.data.message));
      } else {
        setDeleteBannerError(parsed.success ? safeT(locale, parsed.data.message) : t('settings:dangerZone.delete.toast.failed'));
      }
      // Deliberately does NOT clear `confirmName`/`confirmEmail` (ux.md:
      // preserve user input across a validation miss).
    } finally {
      setDeleting(false);
    }
  }

  async function handleExport(): Promise<void> {
    setExportPending(true);
    setExportError(undefined);
    try {
      await requestTyped(wsClient, 'account.exportData', {});
      toast.push({ tone: 'success', message: t('settings:dangerZone.export.toast.started') });
    } catch {
      setExportError(t('settings:dangerZone.export.toast.failed'));
    } finally {
      setExportPending(false);
    }
  }

  if (farewell) {
    return (
      <section className={cn(styles['card'], styles['farewell'])} aria-live="polite">
        <Mascot pose="waving" size={120} alt="" muted animate={false} />
        <h2 className={cn(styles['title'])}>{t('settings:dangerZone.farewell.title')}</h2>
        <p className={cn(styles['description'])}>{t('settings:dangerZone.farewell.description')}</p>
      </section>
    );
  }

  return (
    <section className={cn(styles['card'])}>
      <h2 className={cn(styles['title'])}>{t('settings:dangerZone.title')}</h2>

      <div className={cn(styles['box'])}>
        <div className={cn(styles['row'])}>
          <div className={cn(styles['rowText'])}>
            <h3 className={cn(styles['rowTitle'])}>{t('settings:dangerZone.delete.title')}</h3>
            <p className={cn(styles['rowDescription'])}>{t('settings:dangerZone.delete.description')}</p>
          </div>
          <Button variant="destructiveOutline" onClick={() => setDeleteModalOpen(true)}>
            {t('settings:dangerZone.delete.cta')}
          </Button>
        </div>
      </div>

      <div className={cn(styles['exportRow'])}>
        <p className={cn(styles['rowDescription'])}>{t('settings:dangerZone.export.description')}</p>
        <div className={cn(styles['rowAction'])}>
          <button
            type="button"
            className={cn(styles['exportLink'])}
            disabled={exportPending}
            onClick={() => void handleExport()}
          >
            {t('settings:dangerZone.export.cta')}
          </button>
          {exportError && (
            <p role="alert" className={cn(styles['error'])}>
              {exportError}
            </p>
          )}
        </div>
      </div>

      <Modal
        open={deleteModalOpen}
        onClose={resetDeleteModal}
        title={t('settings:dangerZone.delete.modal.title')}
        description={t('settings:dangerZone.delete.modal.description')}
        size="sm"
      >
        <form onSubmit={(event) => void handleConfirmDelete(event)} noValidate className={cn(styles['form'])}>
          <Input
            label={t('settings:dangerZone.delete.modal.nameLabel', { name: confirmNameTarget })}
            value={confirmName}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setConfirmName(event.target.value)}
            {...(deleteFieldErrors.name !== undefined ? { error: deleteFieldErrors.name } : {})}
            required
            autoComplete="off"
          />
          <Input
            label={t('settings:dangerZone.delete.modal.emailLabel', { email: profile?.email ?? '' })}
            type="email"
            value={confirmEmail}
            onChange={(event: ChangeEvent<HTMLInputElement>) => setConfirmEmail(event.target.value)}
            {...(deleteFieldErrors.email !== undefined ? { error: deleteFieldErrors.email } : {})}
            required
            autoComplete="off"
          />
          {deleteBannerError && (
            <p role="alert" className={cn(styles['error'])}>
              {deleteBannerError}
            </p>
          )}
          <div className={cn(styles['modalActions'])}>
            <Button type="button" variant="secondary" onClick={resetDeleteModal} disabled={deleting}>
              {tGlobal('common:action.cancel')}
            </Button>
            <Button type="submit" variant="destructive" state={deleting ? 'loading' : 'idle'} disabled={!canSubmitDelete}>
              {t('settings:dangerZone.delete.modal.confirmCta')}
            </Button>
          </div>
        </form>
      </Modal>
    </section>
  );
}
